/**
 * OCR Service - Updated to use Redis session-based business context
 * No more hardcoded business_id parameters
 */
import { autoCompressImage } from '../utils/imageCompression';
import http from './httpClient';

class OCRService {
  static async processImage(imageFile, options = {}) {
    // Compress image before uploading if not disabled
    let fileToUpload = imageFile;
    // Skip compression for PDFs
    const isPDF = imageFile && (imageFile.type === 'application/pdf' || (imageFile.name || '').toLowerCase().endsWith('.pdf'));
    if (!isPDF && options.skipCompression !== true) {
      try {
        fileToUpload = await autoCompressImage(imageFile);

      } catch (error) {
        console.warn('⚠️ Image compression failed, using original image:', error);
        // Continue with original file if compression fails
      }
    }

    const formData = new FormData();
    formData.append('image', fileToUpload);
    formData.append('language', options.language || 'eng');
    formData.append('mode', (options.mode || 'auto'));


    try {
      const result = await http.postFormData('/ocr/upload', formData);
      return result;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again with a smaller file.');
      }
      if (error && typeof error.message === 'string' && error.message.includes('fetch')) {
        throw new Error('Cannot connect to server. Please check your network connection and ensure the backend is running.');
      }
      console.error('❌ OCR Service Error:', error);
      throw error;
    }
  }

  static async processImageFromURL(imageUrl, options = {}) {
    try {
      const result = await http.post('/ocr/process-url', {
        imageUrl,
        language: options.language || 'eng'
      });
      return result;
    } catch (error) {
      console.error('OCR Service Error:', error);
      throw error;
    }
  }

  static async processBase64Image(base64Data, options = {}) {
    try {
      const result = await http.post('/ocr/process-base64', {
        imageData: base64Data,
        language: options.language || 'eng'
      });
      return result;
    } catch (error) {
      console.error('OCR Service Error:', error);
      throw error;
    }
  }

  static async processAndCreateStock(imageFile, options = {}) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('shift', options.shift || 'Morning');
    formData.append('autoSubmit', options.autoSubmit || false);

    try {
      const result = await http.postFormData('/ocr/process-and-create-stock', formData);
      return result;
    } catch (error) {
      console.error('OCR Service Error:', error);
      throw error;
    }
  }

  static async validateAPIKey() {
    try {
      const result = await http.get('/ocr/validate-api');
      return result;
    } catch (error) {
      console.error('OCR Service Error:', error);
      throw error;
    }
  }

  // Get uploaded images - business_id now from session
  static async getUploadedImages(scanType = null, limit = 50) {
    try {
      const params = new URLSearchParams({ limit });
      if (scanType) params.append('scan_type', scanType);
      const data = await http.get(`/ocr/images?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch uploaded images');
      return data;
    } catch (error) {
      console.error('Error fetching uploaded images:', error);
      throw error;
    }
  }

  // Process OCR for specific image
  static async processOCR(imageId) {
    try {
      const data = await http.post(`/ocr/process/${imageId}`);
      if (!data.success) throw new Error(data.error || 'Failed to process OCR');
      return data;
    } catch (error) {
      console.error('Error processing OCR:', error);
      throw error;
    }
  }

  // Upload image file - business_id now from session
  static async uploadImage(imageFile, scanType = 'Other') {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('scan_type', scanType);

      const data = await http.postFormData('/ocr/upload', formData);
      if (!data.success) throw new Error(data.error || 'Failed to upload image');
      return data;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Process sales report (legacy)
  static async processSalesReport(imageFile) {
    // Skip compression for PDFs
    const isPDF = imageFile && (imageFile.type === 'application/pdf' || (imageFile.name || '').toLowerCase().endsWith('.pdf'));
    const formData = new FormData();
    formData.append('image', imageFile);
    try {
      const data = await http.postFormData('/sales-report/process-sales-report', formData);
      return data;
    } catch (error) {
      console.error('Error processing sales report:', error);
      throw error;
    }
  }

  // Process sales report V2 - Dedicated TSR Parser
  /**
   * @param {File} imageFile
   * @param {object} [opts]
   * @param {boolean} [opts.confirmReupload] the owner answered the "this file was
   *        already used" warning with Continue. Sent as a field so the server can
   *        skip its pre-parse check for this one request.
   */
  static async processSalesReportV2(imageFile, { confirmReupload = false, tsrDate = null, outlet = null } = {}) {


    const formData = new FormData();
    formData.append('file', imageFile); // Note: V2 uses 'file' instead of 'image'
    if (confirmReupload) formData.append('confirmReupload', 'true');
    // The TSR day being filed: a file that states its own day is refused by the
    // server unless the two agree.
    if (tsrDate) formData.append('tsrDate', tsrDate);
    // A POS outlet report is filed one outlet at a time: the chosen outlet.
    if (outlet) formData.append('outlet', outlet);

    try {
      const data = await http.postFormData('/sales-report/v2/process-tsr', formData);

      if (!data.success) {
        throw new Error(data.error || 'Failed to process sales report');
      }

      // A day-wise (multi-date) report is a different shape entirely: its
      // payload is the per-date breakdown at the top level, not a single
      // `data.items` list. Hand it back whole — collapsing it into parsedData
      // would drop the dates, which is the one thing that makes it day-wise.
      if (data.is_day_wise) {
        return { success: true, is_day_wise: true, ...data };
      }

      // A POS outlet report with no outlet chosen yet: the outlet picker.
      if (data.is_outlet_report) {
        return { success: true, is_outlet_report: true, ...data };
      }

      // Map V2 format to expected format
      return {
        success: true,
        parsedData: data.data,
        salesData: data.data, // Alias for compatibility
        ocrData: {
          rawText: data.metadata?.rawText || ''
        },
        metadata: data.metadata,
        // Byte identity of the upload. Carried to /tsr/submit so the file can be
        // recognised if it is ever uploaded again for another day.
        sourceSha: data.metadata?.sourceSha || null
      };
    } catch (error) {
      console.error('❌ Error processing sales report V2:', error);
      throw error;
    }
  }

}

export default OCRService;